// src/pages/CharacterCreatePage.tsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createCharacter } from '../services/characterService';
import { ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';

const CharacterCreatePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    nameReading: '',
    bio: '',
    species: '',
    gender: '',
    age: '',
    personality: [] as string[],
    likes: [] as string[],
    dislikes: [] as string[],
    allowFanart: true,
    isPublic: false,
  });
  const [personalityInput, setPersonalityInput] = useState('');
  const [likesInput, setLikesInput] = useState('');
  const [dislikesInput, setDislikesInput] = useState('');

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">ログインが必要です</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-gray-900 text-white px-6 py-2 rounded-full"
          >
            ログインへ
          </button>
        </div>
      </div>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addTag = (field: 'personality' | 'likes' | 'dislikes', value: string) => {
    if (!value.trim()) return;
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], value.trim()]
    }));
  };

  const removeTag = (field: 'personality' | 'likes' | 'dislikes', index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!user || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const characterId = await createCharacter(user.uid, {
        name: formData.name,
        nameReading: formData.nameReading || undefined,
        bio: formData.bio,
        species: formData.species || undefined,
        gender: formData.gender || undefined,
        age: formData.age || undefined,
        personality: formData.personality.length > 0 ? formData.personality : undefined,
        likes: formData.likes.length > 0 ? formData.likes : undefined,
        dislikes: formData.dislikes.length > 0 ? formData.dislikes : undefined,
        allowFanart: formData.allowFanart,
        isPublic: formData.isPublic,
        tags: [],
        avatarUrl: '',
      });
      navigate(`/character/${characterId}`);
    } catch (error) {
      console.error('Error creating character:', error);
      alert('キャラクターの作成に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="p-2 -ml-2 rounded-full hover:bg-gray-100">
              <ChevronLeftIcon className="w-6 h-6 text-gray-700" />
            </Link>
            <h1 className="text-lg font-bold text-gray-900">キャラクター作成</h1>
            <div className="w-10" />
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-colors ${
                  step >= s
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div
                  className={`w-12 sm:w-16 h-1 transition-colors ${
                    step > s ? 'bg-gray-900' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
          {step === 1 && (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-6">基本情報</h2>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    キャラクター名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    maxLength={30}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-shadow"
                    placeholder="例: ミク"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{formData.name.length}/30</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    読み仮名
                  </label>
                  <input
                    type="text"
                    name="nameReading"
                    value={formData.nameReading}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-shadow"
                    placeholder="例: みく"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    自己紹介（キャラクター視点）
                  </label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={100}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-shadow resize-none"
                    placeholder="キャラクターの口調で自己紹介を書いてみましょう"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{formData.bio.length}/100</p>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-6">キャラクター設定</h2>

              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      種族
                    </label>
                    <input
                      type="text"
                      name="species"
                      value={formData.species}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-shadow"
                      placeholder="例: 人間"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      性別
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-shadow"
                    >
                      <option value="">選択</option>
                      <option value="女性">女性</option>
                      <option value="男性">男性</option>
                      <option value="その他">その他</option>
                      <option value="不明">不明</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    年齢
                  </label>
                  <input
                    type="text"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-shadow"
                    placeholder="例: 16歳"
                  />
                </div>

                {/* Personality Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    性格
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={personalityInput}
                      onChange={(e) => setPersonalityInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTag('personality', personalityInput);
                          setPersonalityInput('');
                        }
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-shadow"
                      placeholder="例: 明るい"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        addTag('personality', personalityInput);
                        setPersonalityInput('');
                      }}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
                    >
                      追加
                    </button>
                  </div>
                  {formData.personality.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.personality.map((tag, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                          {tag}
                          <button onClick={() => removeTag('personality', i)}>
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Likes Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    好きなもの
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={likesInput}
                      onChange={(e) => setLikesInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTag('likes', likesInput);
                          setLikesInput('');
                        }
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-shadow"
                      placeholder="例: 音楽"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        addTag('likes', likesInput);
                        setLikesInput('');
                      }}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
                    >
                      追加
                    </button>
                  </div>
                  {formData.likes.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.likes.map((tag, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 text-green-600 text-sm rounded-full">
                          {tag}
                          <button onClick={() => removeTag('likes', i)}>
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Dislikes Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    嫌いなもの
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={dislikesInput}
                      onChange={(e) => setDislikesInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTag('dislikes', dislikesInput);
                          setDislikesInput('');
                        }
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-shadow"
                      placeholder="例: 虫"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        addTag('dislikes', dislikesInput);
                        setDislikesInput('');
                      }}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
                    >
                      追加
                    </button>
                  </div>
                  {formData.dislikes.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.dislikes.map((tag, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 text-red-600 text-sm rounded-full">
                          {tag}
                          <button onClick={() => removeTag('dislikes', i)}>
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-6">公開設定</h2>

              <div className="space-y-5">
                {/* Fanart Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-medium text-gray-900">ファンアートを許可</p>
                    <p className="text-sm text-gray-500">
                      他のユーザーがファンアートを投稿できます
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.allowFanart}
                      onChange={(e) => setFormData(prev => ({ ...prev, allowFanart: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-900"></div>
                  </label>
                </div>

                {/* Public Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-medium text-gray-900">今すぐ公開</p>
                    <p className="text-sm text-gray-500">
                      オフの場合は下書きとして保存されます
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.isPublic}
                      onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gray-900"></div>
                  </label>
                </div>

                {/* Preview */}
                <div className="border border-gray-200 rounded-xl p-6 mt-6">
                  <h3 className="text-sm font-medium text-gray-500 mb-4">プレビュー</h3>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl text-white font-bold">
                        {formData.name.charAt(0) || '?'}
                      </span>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">
                        {formData.name || 'キャラクター名'}
                      </p>
                      <p className="text-gray-500 text-sm">{formData.species || '種族未設定'}</p>
                    </div>
                  </div>
                  {formData.bio && (
                    <p className="mt-4 text-gray-600 text-sm">{formData.bio}</p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
            {step > 1 ? (
              <button
                onClick={() => setStep(step - 1)}
                className="px-6 py-2.5 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors font-medium"
              >
                戻る
              </button>
            ) : (
              <div />
            )}

            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && !formData.name}
                className="bg-gray-900 text-white px-6 py-2.5 rounded-full hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                次へ
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-gray-900 text-white px-8 py-2.5 rounded-full hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isSubmitting ? '作成中...' : '作成する'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharacterCreatePage;
